/**
 * EP-20 knock-off (screening) questions — the pure half of the recruiter's
 * editor (`ScreeningQuestionsField`) and of the candidate Gate's screening step.
 *
 * A test may ask up to `MAX_SCREENING_QUESTIONS` single-choice questions right
 * before it starts; each marks the answers that END the candidate's session.
 * The server (backend/screening.py) is the trust boundary and owns every rule;
 * these mirrors only gate Save / Continue and show the same message inline, so
 * a mismatch costs a round-trip, never correctness. Keep the messages
 * identical to the server's.
 *
 * ⚠ `disqualifying` is the answer key. The candidate side only ever sees
 * `ScreeningQuestionPublic` (`/preview`), which has no such field.
 */

import type {
  ScreeningQuestion,
  ScreeningQuestionPublic,
} from "@/lib/api";

export type { ScreeningQuestion, ScreeningQuestionPublic };

// Mirrors of backend/screening.py.
export const MAX_SCREENING_QUESTIONS = 5;
export const MIN_OPTIONS = 2;
export const MAX_OPTIONS = 6;
export const MAX_PROMPT_LEN = 200;
export const MAX_OPTION_LEN = 80;

function randomHex(bytes: number): string {
  const buf = new Uint8Array(bytes);
  if (typeof crypto !== "undefined" && crypto.getRandomValues) {
    crypto.getRandomValues(buf);
  } else {
    for (let i = 0; i < buf.length; i += 1) {
      buf[i] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(buf, (b) => b.toString(16).padStart(2, "0")).join("");
}

/** A fresh question: the commonest screening shape (Yes / No, "No" ends it),
 *  with a blank prompt so the row is invalid until the recruiter writes one. */
export function newScreeningQuestion(): ScreeningQuestion {
  return {
    key: `sq_${randomHex(3)}`,
    prompt: "",
    options: ["Yes", "No"],
    disqualifying: ["No"],
  };
}

/** What Save sends: trimmed text, and `disqualifying` re-derived from the
 *  options in their order — so renaming or deleting an option can never leave
 *  a stale knock-out behind. */
export function normalizeScreeningQuestion(q: ScreeningQuestion): ScreeningQuestion {
  const options = q.options.map((o) => o.trim());
  const chosen = new Set(q.disqualifying.map((o) => o.trim()));
  return {
    key: q.key,
    prompt: q.prompt.trim(),
    options,
    disqualifying: options.filter((o) => chosen.has(o)),
  };
}

/** The first rule a question breaks, in the server's words; null when valid. */
export function screeningQuestionError(raw: ScreeningQuestion): string | null {
  const q = normalizeScreeningQuestion(raw);
  if (!q.prompt) return "Every screening question needs a question text.";
  if (q.prompt.length > MAX_PROMPT_LEN)
    return `A screening question can be at most ${MAX_PROMPT_LEN} characters.`;
  if (q.options.some((o) => !o)) return "A screening answer cannot be blank.";
  if (q.options.some((o) => o.length > MAX_OPTION_LEN))
    return `A screening answer can be at most ${MAX_OPTION_LEN} characters.`;
  if (q.options.length < MIN_OPTIONS || q.options.length > MAX_OPTIONS)
    return `A screening question needs ${MIN_OPTIONS} to ${MAX_OPTIONS} answers.`;
  if (new Set(q.options.map((o) => o.toLowerCase())).size !== q.options.length)
    return "A screening question cannot repeat an answer.";
  if (q.disqualifying.length === 0)
    return "Mark at least one answer that ends the candidate's session.";
  if (q.disqualifying.length === q.options.length)
    return "At least one answer must let the candidate continue.";
  return null;
}

/** The first problem with the whole block; null when Save may go ahead. */
export function screeningListError(
  enabled: boolean,
  questions: ScreeningQuestion[],
): string | null {
  if (questions.length > MAX_SCREENING_QUESTIONS)
    return `At most ${MAX_SCREENING_QUESTIONS} screening questions per test.`;
  if (enabled && questions.length === 0)
    return "Add at least one screening question before switching screening on.";
  // OFF hides the editor (and ticking seeds a blank question), so a broken
  // question the recruiter can no longer see must not refuse the save —
  // `screeningQuestionsToSend` leaves it out (Sheet #193).
  if (!enabled) return null;
  for (const q of questions) {
    const err = screeningQuestionError(q);
    if (err) return err;
  }
  return null;
}

/** What Save sends as `screening_questions`: every question while ON; while
 *  OFF only the complete ones, so a recruiter who authored and then unticked
 *  keeps them without an unfinished draft 422-ing the save. null for none. */
export function screeningQuestionsToSend(
  enabled: boolean,
  questions: ScreeningQuestion[],
): ScreeningQuestion[] | null {
  const kept = enabled
    ? questions
    : questions.filter((q) => screeningQuestionError(q) === null);
  return kept.length ? kept.map(normalizeScreeningQuestion) : null;
}

/** Dirty-compare on what Save would SEND (the hydrated-form rule: a
 *  whitespace-only edit is not a change). */
export function screeningQuestionsEqual(
  a: ScreeningQuestion[],
  b: ScreeningQuestion[],
): boolean {
  return (
    JSON.stringify(a.map(normalizeScreeningQuestion)) ===
    JSON.stringify(b.map(normalizeScreeningQuestion))
  );
}

/** The editor's dirty rule for the pair: a change only when what Save would
 *  SEND differs from the stored row. Compared on the raw list, a draft the
 *  recruiter ticked, left blank and unticked lit Save on its own — and kept it
 *  lit AFTER a successful save, because the row comes back without the draft
 *  while the editor (which never re-hydrates after saving) still holds it
 *  (Sheet #193 verify, 2026-10-21). */
export function screeningSettingsDirty(
  enabled: boolean,
  questions: ScreeningQuestion[],
  stored: {
    screening_enabled?: boolean | null;
    screening_questions?: ScreeningQuestion[] | null;
  },
): boolean {
  return (
    enabled !== (stored.screening_enabled ?? false) ||
    !screeningQuestionsEqual(
      screeningQuestionsToSend(enabled, questions) ?? [],
      stored.screening_questions ?? [],
    )
  );
}

/** Continue is live once every question has one of its own options chosen. */
export function screeningAnswersComplete(
  questions: ScreeningQuestionPublic[],
  answers: Record<string, string>,
): boolean {
  return questions.every((q) => q.options.includes(answers[q.key] ?? ""));
}
